import AkSelect, { type SelectProps as AkSelectProps } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import { useStateWithOverride } from '@databases/react';
import { MenuPortalContext } from '@databases/react/menuPortal';
import React, {
  type ComponentType,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useContext,
  useCallback,
} from 'react';
import { useAsync } from 'react-async';
import { useIntl } from 'react-intl-next';
import { type GroupTypeBase, type NamedProps, type OptionTypeBase } from 'react-select';

import { messages } from './messages';

export { components } from '@atlaskit/select';
export type {
  ActionMeta,
  FormatOptionLabelMeta,
  SelectComponentsConfig,
  StylesConfig,
} from '@atlaskit/select';

const defaultModifyOptions = (options: any) => options;
const filterOption = () => true;
const LoadingIndicator = () => <Spinner size="small" />;

// react-select exports a strange SelectProps type that extends Record<string, any>, which messes up the typing.
// See https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34959
// For now we use this as a workaround to have proper typing.
type FixedSelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean = false,
  GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
> = NamedProps<OptionType, IsMulti, GroupType> &
  Pick<AkSelectProps<OptionType, IsMulti>, 'spacing' | 'validationState' | 'appearance'>;

export type SelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean = false,
  GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
> = FixedSelectProps<OptionType, IsMulti, GroupType> & {
  isSeamless?: boolean;
  isInvalid?: boolean;
  /** If true, the input value is preserved when closing the menu. */
  preserveInputValue?: boolean;
  /** Whether to pull the current value from the async options by the current value's ID */
  fetchValueFromOptions?: boolean;
  /**
   * Custom data attribute to add to the select container div.
   * Marks interactive elements to prevent event forwarding in embedded contexts.
   * Defaults to true. Set to false to allow event forwarding through this select.
   */
  'data-interactive'?: boolean;
};

function useMenuOpen({
  menuIsOpen: menuIsOpenProp,
  defaultMenuIsOpen,
  onMenuOpen,
  onMenuClose,
}: Pick<
  FixedSelectProps<any>,
  'menuIsOpen' | 'defaultMenuIsOpen' | 'onMenuOpen' | 'onMenuClose'
>): Pick<FixedSelectProps<any>, 'menuIsOpen' | 'onMenuOpen' | 'onMenuClose'> {
  const [menuIsOpen, setMenuIsOpen] = useStateWithOverride(
    defaultMenuIsOpen ?? false,
    menuIsOpenProp
  );

  const handleMenuOpen = useCallback(() => {
    setMenuIsOpen(true);
    onMenuOpen?.();
  }, [onMenuOpen, setMenuIsOpen]);

  const handleMenuClose = useCallback(() => {
    /** wrap with setTimeout to be able to retrieve the child node of menuPortalTarget in the {@link @databases/react useOnClickOutsideRef} function */
    setTimeout(() => {
      setMenuIsOpen(false);
      onMenuClose?.();
    }, 0);
  }, [onMenuClose, setMenuIsOpen]);

  return useMemo(
    () => ({
      menuIsOpen: menuIsOpen,
      onMenuOpen: handleMenuOpen,
      onMenuClose: handleMenuClose,
    }),
    [handleMenuClose, handleMenuOpen, menuIsOpen]
  );
}

function useInputValue<OptionType extends OptionTypeBase>({
  inputValue: inputValueProp,
  onInputChange,
  preserveInputValue,
}: Pick<
  SelectProps<OptionType>,
  'inputValue' | 'onInputChange' | 'preserveInputValue'
>): Required<Pick<FixedSelectProps<OptionType>, 'inputValue' | 'onInputChange'>> {
  const [inputValue, setInputValue] = useStateWithOverride('', inputValueProp);
  const lastInputChangeEvent =
    useRef<Parameters<NonNullable<AkSelectProps<OptionType>['onInputChange']>>>();

  const handleInputChange: NonNullable<AkSelectProps<OptionType>['onInputChange']> =
    useCallback(
      (val, meta) => {
        // meta.action === 'input-change' means that the input value was changed due to the user typing something
        // into the input field. All other cases mean that the input value is cleared due to closing the menu,
        // blurring the field, ...
        if (!preserveInputValue || meta.action === 'input-change') {
          setInputValue(val);
        }
        lastInputChangeEvent.current = [val, meta];
        onInputChange?.(val, meta);
      },
      [onInputChange, preserveInputValue, setInputValue]
    );

  useEffect(() => {
    if (!preserveInputValue && lastInputChangeEvent.current) {
      handleInputChange(...lastInputChangeEvent.current);
    }
  }, [preserveInputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(
    () => ({ inputValue, onInputChange: handleInputChange }),
    [handleInputChange, inputValue]
  );
}

/**
 * Like an AtlasKit select, but with the following differences:
 * - Proper typing (a bug in the typings of react-select allows specifying any props)
 * - Supports isInvalid prop like other AtlasKit components
 * - Uses menuPortalTarget
 * - Supports the open menu padding
 */
export default function Select<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean = false,
  GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
>({
  isInvalid,
  inputValue,
  onInputChange,
  preserveInputValue,
  components,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
  defaultMenuIsOpen,
  isMulti,
  'data-interactive': dataInteractive = true,
  ...otherProps
}: SelectProps<OptionType, IsMulti, GroupType>): ReactElement {
  const FixedSelect = AkSelect as ComponentType<
    FixedSelectProps<OptionType, IsMulti, GroupType>
  >;

  const menuOpenProps = useMenuOpen({
    menuIsOpen,
    onMenuOpen,
    onMenuClose,
    defaultMenuIsOpen,
  });

  const inputValueProps = useInputValue({
    inputValue,
    onInputChange,
    preserveInputValue,
  });

  const menuPortalTarget = useContext(MenuPortalContext);

  const containerProps = dataInteractive ? { 'data-interactive': true } : {};

  return (
    <div {...containerProps}>
      <FixedSelect
        menuPlacement="auto"
        menuPosition="absolute"
        appearance={isMulti ? 'subtle' : 'default'}
        menuPortalTarget={menuPortalTarget}
        validationState={isInvalid ? 'error' : undefined}
        components={{ LoadingIndicator, ...components }}
        isMulti={isMulti}
        {...otherProps}
        {...inputValueProps}
        {...menuOpenProps}
      />
    </div>
  );
}

export interface AsyncSelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean = false,
  GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
> extends Omit<
    SelectProps<OptionType, IsMulti, GroupType>,
    'options' | 'filterOption' | 'getOptionValue'
  > {
  loadOptions: (inputValue: string) => Promise<Array<OptionType | GroupType>>;
  getOptionValue: (option: OptionType) => string;
  isOptionsLoading?: boolean;

  /**
   * Allows to modify the list of options, for example to add additional ones. This allows to decouple
   * the modifications from the async loading of options, so that when the modifications depend on an
   * external parameter, the options don't have to be loaded again in order to apply the new modifications
   * when the external parameter changes.
   */
  modifyOptions?: (
    options: Array<OptionType | GroupType>,
    inputValue: string
  ) => Array<OptionType | GroupType>;
}

/**
 * Like an AtlasKit AsyncSelect, but with the following differences:
 * - Proper typing (a bug in the typings of react-select allows specifying any props)
 * - Supports isInvalid prop like other AtlasKit components
 * - The options are refetched when the loadOptions function identity changes (https://github.com/JedWatson/react-select/issues/1581)
 * - Multiple create options can be specified by using modifyOptions prop (https://github.com/JedWatson/react-select/issues/1774)
 * - While the input value is empty, if the selected value(s) do not appear in the options, they are prepended to the list of options.
 * - Uses menuPortalTarget (also enables support for useHasOpenMenu() hook)
 */
export function AsyncSelect<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean = false,
  GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
>({
  value,
  getOptionValue,
  inputValue,
  loadOptions,
  modifyOptions = defaultModifyOptions,
  onInputChange,
  preserveInputValue,
  isLoading,
  noOptionsMessage,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
  defaultMenuIsOpen,
  fetchValueFromOptions,
  isOptionsLoading,
  menuPortalTarget,
  ...otherProps
}: AsyncSelectProps<OptionType, IsMulti, GroupType>): ReactElement {
  const { formatMessage } = useIntl();

  const menuOpenProps = useMenuOpen({
    menuIsOpen,
    onMenuOpen,
    onMenuClose,
    defaultMenuIsOpen,
  });

  const inputValueProps = useInputValue({
    inputValue,
    onInputChange,
    preserveInputValue,
  });

  const optionsAsync = useAsync({
    promiseFn: useCallback(async () => {
      return await loadOptions(inputValueProps.inputValue);
    }, [inputValueProps.inputValue, loadOptions]),
  });

  const options = useMemo(() => {
    if (isOptionsLoading || optionsAsync.isLoading) {
      return [];
    }
    const modifiedOptions = [
      ...modifyOptions(optionsAsync.data || [], inputValueProps.inputValue),
    ];
    if (!inputValueProps.inputValue) {
      // If any selected value is not part of the options, prepend it
      const keys = new Set(
        modifiedOptions
          .flatMap((op) => ('options' in op ? op.options : [op]))
          .map((op) => getOptionValue(op))
      );
      modifiedOptions.unshift(
        ...(Array.isArray(value) ? value : value ? [value] : []).flatMap((val) => {
          return keys.has(getOptionValue(val)) ? [] : [val];
        })
      );
    }
    return modifiedOptions;
  }, [
    getOptionValue,
    inputValueProps.inputValue,
    isOptionsLoading,
    modifyOptions,
    optionsAsync.data,
    optionsAsync.isLoading,
    value,
  ]);

  const resolvedNoOptionsMessage = useMemo(
    () =>
      isOptionsLoading || optionsAsync.isLoading
        ? () => formatMessage(messages['generic-component.select-picker-loading-text'])
        : noOptionsMessage,
    [formatMessage, isOptionsLoading, noOptionsMessage, optionsAsync.isLoading]
  );

  const findValueInOptions = useCallback(
    (
      options: (OptionType | GroupType)[] | OptionType | GroupType,
      values: OptionType | readonly OptionType[] | undefined | null
    ): OptionType | readonly OptionType[] => {
      const findValueNested = (
        options: (OptionType | GroupType)[] | OptionType | GroupType,
        values: OptionType | readonly OptionType[] | undefined | null
      ) => {
        const foundOptions: OptionType[] = [];
        if (!values) {
          return foundOptions;
        }

        (Array.isArray(options) ? options : [options]).forEach((optVal) => {
          if ('options' in optVal) {
            foundOptions.push(...findValueNested(optVal.options, values));
          } else {
            (Array.isArray(values) ? values : [values]).forEach((valVal) => {
              if (getOptionValue(optVal) === getOptionValue(valVal)) {
                foundOptions.push(optVal);
              }
            });
          }
        });

        return foundOptions;
      };

      return findValueNested(options, values);
    },
    [getOptionValue]
  );

  const foundValue = useMemo(
    () => (fetchValueFromOptions ? findValueInOptions(options, value) : value),
    [fetchValueFromOptions, options, value, findValueInOptions]
  );

  const defaultMenuPortalTarget = useContext(MenuPortalContext);
  return (
    <Select<OptionType, IsMulti, GroupType>
      {...otherProps}
      menuPlacement="auto"
      menuPosition="absolute"
      // Allow for the passing of null menuPortalTarget
      menuPortalTarget={
        menuPortalTarget !== undefined ? menuPortalTarget : defaultMenuPortalTarget
      }
      value={foundValue}
      getOptionValue={getOptionValue}
      isLoading={
        (menuOpenProps.menuIsOpen && optionsAsync.isLoading) ||
        isLoading ||
        (fetchValueFromOptions && optionsAsync.isLoading)
      }
      tabSelectsValue={true}
      options={options}
      noOptionsMessage={resolvedNoOptionsMessage}
      filterOption={filterOption}
      {...inputValueProps}
      {...menuOpenProps}
    />
  );
}
